/**
 * Jurisdiction intake helpers — metadata lives in jurisdiction packages (Mongo / intake.json).
 * This module loads packages and evaluates showWhen rules from package.intake.showWhenRules.
 */

import sfIntakeFallback from './jurisdictions/san-francisco/v1/intake.json';
import demoIntakeFallback from './jurisdictions/demo-generic/v1/intake.json';
import berkeleyIntakeFallback from './jurisdictions/berkeley/v1/intake.json';
import sanJoseIntakeFallback from './jurisdictions/san-jose/v1/intake.json';
import sfCatalogFallback from './jurisdictions/san-francisco/v1/catalog.json';
import berkeleyCatalogFallback from './jurisdictions/berkeley/v1/catalog.json';
import sanJoseCatalogFallback from './jurisdictions/san-jose/v1/catalog.json';
import demoCatalogFallback from './jurisdictions/demo-generic/v1/catalog.json';
import berkeleyDiscoveryExtras from './jurisdictions/berkeley/v1/discovery-extras.json';
import sanJoseDiscoveryExtras from './jurisdictions/san-jose/v1/discovery-extras.json';

const FALLBACK_BY_ID = {
  'san-francisco': sfIntakeFallback,
  'demo-generic': demoIntakeFallback,
  berkeley: berkeleyIntakeFallback,
  'san-jose': sanJoseIntakeFallback,
};

const CATALOG_FALLBACK_BY_ID = {
  'san-francisco': sfCatalogFallback,
  'demo-generic': demoCatalogFallback,
  berkeley: berkeleyCatalogFallback,
  'san-jose': sanJoseCatalogFallback,
};

const EXTRAS_FALLBACK_BY_ID = {
  berkeley: berkeleyDiscoveryExtras,
  'san-jose': sanJoseDiscoveryExtras,
};

const packageCache = new Map();

function clone(x) {
  return JSON.parse(JSON.stringify(x));
}

function normalizePackage(raw, packageId = 'san-francisco') {
  if (!raw || typeof raw !== 'object') return null;
  const intake = raw.intake || (raw.formFields ? raw : {});
  const catalog = raw.catalog || {};
  const id = raw.packageId || raw.id || intake.jurisdictionId || packageId;
  return {
    packageId: id,
    name: raw.name || catalog.name || id,
    version: raw.version || catalog.version || intake.version || '1.0.0',
    timezone: raw.timezone || catalog.timezone,
    disclaimer: raw.disclaimer || catalog.disclaimer || '',
    parcelAdapter: raw.parcelAdapter || intake.parcelAdapter || 'none',
    source: raw.source || 'client-fallback',
    catalog,
    discoveryExtras: raw.discoveryExtras || {},
    intake: {
      ...intake,
      formFields: Array.isArray(intake.formFields) ? intake.formFields : [],
      formElements: Array.isArray(intake.formElements) ? intake.formElements : [],
      inputMapping: Array.isArray(intake.inputMapping) ? intake.inputMapping : [],
      portals: intake.portals && typeof intake.portals === 'object' ? intake.portals : {},
      showWhenRules: intake.showWhenRules || {},
      projectTypeOptions: intake.projectTypeOptions || [],
      clawTools: intake.clawTools || [],
      clawSkill: intake.clawSkill || null,
      map: intake.map || {},
      clearanceColors: intake.clearanceColors || DEFAULT_CLEARANCE_COLORS,
      staffSessionKey: intake.staffSessionKey || 'if-jurisdiction-staff-sessions-v1',
      defaultFormTitle: intake.defaultFormTitle || 'Property details',
      defaultFormDescription: intake.defaultFormDescription || '',
    },
  };
}

export const DEFAULT_CLEARANCE_COLORS = {
  permitted: { fill: '#2e7d32', stroke: '#1b5e20', label: 'Permitted' },
  conditional: { fill: '#ed6c02', stroke: '#e65100', label: 'Conditionally permitted' },
  not_permitted: { fill: '#d32f2f', stroke: '#b71c1c', label: 'Not permitted' },
  unknown: { fill: '#1976d2', stroke: '#0d47a1', label: 'Unknown / not checked' },
};

/** Sync fallback (Studio templates / offline). Prefer fetchJurisdictionPackage at runtime. */
export function getFallbackJurisdictionPackage(jurisdictionId = 'san-francisco') {
  const id = String(jurisdictionId || 'san-francisco').trim() || 'san-francisco';
  const intake = FALLBACK_BY_ID[id] || FALLBACK_BY_ID['san-francisco'];
  const catalog = CATALOG_FALLBACK_BY_ID[id] || { id, name: id };
  const discoveryExtras = EXTRAS_FALLBACK_BY_ID[id] || {};
  return normalizePackage({
    packageId: id,
    name: catalog.name || id,
    catalog: clone(catalog),
    discoveryExtras: clone(discoveryExtras),
    intake: clone(intake),
    parcelAdapter: intake.parcelAdapter || catalog.parcelAdapter || 'none',
    source: 'client-fallback',
  }, id);
}

export function cacheJurisdictionPackage(pkg) {
  if (pkg?.packageId) packageCache.set(pkg.packageId, pkg);
  return pkg;
}

export function getCachedJurisdictionPackage(jurisdictionId = 'san-francisco') {
  const id = String(jurisdictionId || 'san-francisco').trim() || 'san-francisco';
  return packageCache.get(id) || null;
}

/**
 * Load full jurisdiction package (API → cache → bundled JSON fallback).
 */
export async function fetchJurisdictionPackage(jurisdictionId = 'san-francisco', { force = false } = {}) {
  const id = String(jurisdictionId || 'san-francisco').trim() || 'san-francisco';
  if (!force && packageCache.has(id)) return packageCache.get(id);
  try {
    const { getJurisdictionPackageApi } = await import('../api/applicationType.api.js');
    const data = await getJurisdictionPackageApi(id);
    const pkg = normalizePackage(data, id);
    if (pkg) {
      packageCache.set(id, pkg);
      return pkg;
    }
  } catch (_) {
    /* fall through */
  }
  const fallback = getFallbackJurisdictionPackage(id);
  packageCache.set(id, fallback);
  return fallback;
}

function intentHaystack(values = {}) {
  return [
    values.propertyType,
    values.projectType,
    values.description,
    values.proposedUse,
  ].filter(Boolean).join(' ');
}

function projectTypeOf(values = {}) {
  return String(values.propertyType || values.projectType || '').trim();
}

/**
 * Evaluate showWhen using rules from package.intake.showWhenRules (or built-in always).
 */
export function matchesShowWhen(showWhen, values = {}, showWhenRules = {}) {
  if (!showWhen || showWhen === 'always') return true;
  const rule = showWhenRules[showWhen];
  if (!rule || typeof rule !== 'object') return true;
  const type = projectTypeOf(values);
  const text = intentHaystack(values);
  const exclude = Array.isArray(rule.excludePropertyTypes) ? rule.excludePropertyTypes : [];
  if (exclude.includes(type)) return false;
  const types = Array.isArray(rule.propertyTypes) ? rule.propertyTypes : [];
  if (types.length && types.includes(type)) return true;
  const pattern = rule.textPattern;
  if (pattern) {
    try {
      const re = pattern instanceof RegExp ? pattern : new RegExp(pattern, 'i');
      if (re.test(text)) return true;
    } catch (_) {
      /* bad pattern */
    }
  }
  return false;
}

export function isAdvisorFieldVisible(field, values = {}, showWhenRules = {}) {
  return matchesShowWhen(field?.showWhen, values, showWhenRules);
}

/** @deprecated use isAdvisorFieldVisible with package rules */
export function isSfAdvisorFieldVisible(field, values = {}, showWhenRules = null) {
  const rules = showWhenRules
    || getCachedJurisdictionPackage('san-francisco')?.intake?.showWhenRules
    || getFallbackJurisdictionPackage('san-francisco').intake.showWhenRules;
  return isAdvisorFieldVisible(field, values, rules);
}

/** @deprecated */
export function matchesSfShowWhen(showWhen, values = {}) {
  const rules = getCachedJurisdictionPackage('san-francisco')?.intake?.showWhenRules
    || getFallbackJurisdictionPackage('san-francisco').intake.showWhenRules;
  return matchesShowWhen(showWhen, values, rules);
}

export function clearanceColorsFromIntake(intake) {
  return intake?.clearanceColors || DEFAULT_CLEARANCE_COLORS;
}

export function clearanceColor(status, colors = DEFAULT_CLEARANCE_COLORS) {
  const palette = colors || DEFAULT_CLEARANCE_COLORS;
  const key = String(status || 'unknown').toLowerCase().replace(/\s+/g, '_');
  if (key.includes('not')) return palette.not_permitted || DEFAULT_CLEARANCE_COLORS.not_permitted;
  if (key.includes('cond')) return palette.conditional || DEFAULT_CLEARANCE_COLORS.conditional;
  if (key.includes('permit')) return palette.permitted || DEFAULT_CLEARANCE_COLORS.permitted;
  return palette.unknown || DEFAULT_CLEARANCE_COLORS.unknown;
}

export function portalsFromPackage(pkg) {
  return pkg?.intake?.portals || {};
}

export function intakePanelConfigFromPackage(pkg, portalId = null) {
  const intake = pkg?.intake || getFallbackJurisdictionPackage().intake;
  const portals = intake.portals || {};
  const variant = portalId ? portals[portalId] : null;
  const fields = (intake.formFields || []).map((f) => {
    const next = { ...f };
    if (next.id === 'propertyType' && !next.options?.length && intake.projectTypeOptions?.length) {
      next.options = [...intake.projectTypeOptions];
    }
    return next;
  });
  return {
    formTitle: variant?.formTitle || intake.defaultFormTitle || 'Property details',
    formDescription: variant?.formDescription || intake.defaultFormDescription || '',
    formFields: fields,
    inputMapping: (intake.inputMapping || []).map((m) => ({ ...m })),
    showWhenRules: intake.showWhenRules || {},
    map: { ...(intake.map || {}) },
    clawTools: [...(intake.clawTools || [])],
    clawSkill: intake.clawSkill || null,
    clearanceColors: clearanceColorsFromIntake(intake),
    staffSessionKey: intake.staffSessionKey,
    portalVariant: variant || null,
  };
}

/** Sync helpers for Studio templates (bundled SF fallback). */
export function getFallbackIntake() {
  return getFallbackJurisdictionPackage('san-francisco').intake;
}

export const SF_ADVISOR_FORM_FIELDS = getFallbackIntake().formFields;
export const SF_ADVISOR_FORM_ELEMENTS = getFallbackIntake().formElements;
export const SF_ADVISOR_INPUT_MAPPING = getFallbackIntake().inputMapping;
export const SF_ADVISOR_CLAW_TOOLS = getFallbackIntake().clawTools;
export const SF_PROJECT_TYPE_OPTIONS = getFallbackIntake().projectTypeOptions;
export const SF_PORTAL_VARIANTS = getFallbackIntake().portals;
export const LAND_USE_CLEARANCE_COLORS = clearanceColorsFromIntake(getFallbackIntake());
export const SF_STAFF_SESSION_KEY = getFallbackIntake().staffSessionKey
  || 'if-sf-advisor-staff-sessions-v1';
export const SF_FIELD_SHOW_WHEN = {
  always: 'always',
  useChange: 'useChange',
  hospitality: 'hospitality',
  construction: 'construction',
};

export function formFieldsForPortal(portalId = 'business') {
  return intakePanelConfigFromPackage(getFallbackJurisdictionPackage(), portalId).formFields;
}

export function intakePanelConfig(portalId = null) {
  return intakePanelConfigFromPackage(getFallbackJurisdictionPackage(), portalId);
}
